export interface ToolCallConfig {
    /** Whether to show this tool call in the UI */
    showInUI: boolean;
    /** Custom message for when the tool starts */
    startMessage?: string;
    /** Custom message for when the tool completes successfully */
    successMessage?: string;
    /** Custom message for when the tool fails */
    errorMessage?: string;
    /** Whether to show this tool call in system messages */
    showInSystemMessages?: boolean;
}

/** Configuration for each tool call type */
const TOOL_CALL_CONFIG: Record<string, ToolCallConfig> = {
    // Website analysis tools
    websiteScanner: {
        showInUI: true,
        startMessage: 'Analyzing website content...',
        successMessage: 'Website analysis complete',
        errorMessage: 'Failed to analyze website',
        showInSystemMessages: true
    },

    openWorkspace: {
        showInUI: true,
        startMessage: 'Opening workspace...',
        successMessage: 'Opened workspace',
        errorMessage: 'Failed to open workspace',
        showInSystemMessages: true
    },

    createMarketingPlan: {
        showInUI: true,
        startMessage: 'Creating your marketing plan...',
        successMessage: 'Marketing plan created!',
        errorMessage: 'Failed to create marketing plan',
        showInSystemMessages: true
    },

    // UI display tools - typically hidden from user
    showSummaryCard: {
        showInUI: false,
        showInSystemMessages: false
    },

    // Search and research tools
    webSearch: {
        showInUI: true,
        startMessage: 'Searching the web...',
        successMessage: 'Search complete',
        errorMessage: 'Search failed',
        showInSystemMessages: true
    },

    contentAnalyzer: {
        showInUI: true,
        startMessage: 'Analyzing your content...',
        successMessage: 'Content analysis complete',
        errorMessage: 'Failed to analyze content',
        showInSystemMessages: true
    },

    // File and content tools
    fileUpload: {
        showInUI: true,
        startMessage: 'Processing uploaded file...',
        successMessage: 'File processed successfully',
        errorMessage: 'Failed to process file',
        showInSystemMessages: true
    },

    // Campaign and marketing tools
    campaignBuilder: {
        showInUI: true,
        startMessage: 'Building your campaign...',
        successMessage: 'Campaign created successfully',
        errorMessage: 'Failed to create campaign',
        showInSystemMessages: true
    },

    // Asset generation tools
    generateVideo: {
        showInUI: true,
        startMessage: 'Creating your video...',
        successMessage: 'Video generation started',
        errorMessage: 'Failed to start video generation',
        showInSystemMessages: true
    },

    generateImage: {
        showInUI: true,
        startMessage: 'Creating your images...',
        successMessage: 'Image generation started',
        errorMessage: 'Failed to start image generation',
        showInSystemMessages: true
    },

    // Website builder tools
    createWebsite: {
        showInUI: true,
        startMessage: 'Creating your website...',
        successMessage: 'Website created successfully',
        errorMessage: 'Failed to create website',
        showInSystemMessages: true
    },

    updateWebsite: {
        showInUI: true,
        startMessage: 'Updating website...',
        successMessage: 'Website updated successfully',
        errorMessage: 'Failed to update website',
        showInSystemMessages: true
    },

    searchGenreIndicators: {
        showInUI: true,
        startMessage: 'Researching your audience...',
        successMessage: 'Found your audience!',
        errorMessage: 'Found your audience',
        showInSystemMessages: true
    },

    websearchproxy: {
        showInUI: true,
        startMessage: 'Searching the web...',
        successMessage: 'Search complete',
        errorMessage: 'Search failed',
        showInSystemMessages: true
    },

    getMusicInfo: {
        showInUI: true,
        startMessage: 'Searching for your release...',
        successMessage: 'Found your release!',
        errorMessage: 'Failed to find your release',
        showInSystemMessages: true
    },

    startCampaignDraft: {
        showInUI: true,
        startMessage: 'Creating a campaign draft...',
        successMessage: 'Campaign draft created!',
        errorMessage: 'Failed to create campaign draft',
        showInSystemMessages: true
    },

    setCampaignStrategy: {
        showInUI: true,
        startMessage: 'Saving your campaign strategy...',
        successMessage: 'Campaign strategy updated!',
        errorMessage: 'Failed to save campaign strategy',
        showInSystemMessages: true
    },

    searchOnSocials: {
        showInUI: true,
        startMessage: 'Searching social media...',
        successMessage: 'Search complete!',
        errorMessage: 'Search failed',
        showInSystemMessages: true
    },

    sendErrorReport: {
        showInUI: true,
        startMessage: 'Sending error report...',
        successMessage: 'Error report sent!',
        errorMessage: 'Failed to send error report',
        showInSystemMessages: true
    },
    
    addExecutableAdAction: {
        showInUI: true,
        startMessage: 'Adding executable ad action...',
        successMessage: 'Executable ad action added!',
        errorMessage: 'Failed to add executable ad action',
        showInSystemMessages: true
    },

    addOnPlatformAdAction: {
        showInUI: true,
        startMessage: 'Adding on-platform ad action...',
        successMessage: 'On-platform ad action added!',
        errorMessage: 'Failed to add on-platform ad action',
        showInSystemMessages: true
    },

    addSymphonyToolAction: {
        showInUI: true,
        startMessage: 'Adding Symphony tool action...',
        successMessage: 'Symphony tool action added!',
        errorMessage: 'Failed to add Symphony tool action',
        showInSystemMessages: true
    },

    addContentStrategyAction: {
        showInUI: true,
        startMessage: 'Adding content strategy action...',
        successMessage: 'Content strategy action added!',
        errorMessage: 'Failed to add content strategy action',
        showInSystemMessages: true
    },

    addExternalStrategyAction: {
        showInUI: true,
        startMessage: 'Adding external strategy action...',
        successMessage: 'External strategy action added!',
        errorMessage: 'Failed to add external strategy action',
        showInSystemMessages: true
    },


    connectToBrand: {
        showInUI: true,
        startMessage: 'Updating your profile...',
        successMessage: 'Profile updated!',
        errorMessage: 'Failed to update your profile',
        showInSystemMessages: true
    },
    setCampaignLogistics: {
        showInUI: true,
        startMessage: 'Saving your campaign logistics...',
        successMessage: 'Campaign logistics updated!',
        errorMessage: 'Failed to save campaign strategy',
        showInSystemMessages: true
    },

    analyzeTikTokProfile: {
        showInUI: true,
        startMessage: 'Analyzing your TikTok profile...',
        successMessage: 'TikTok profile analyzed!',
        errorMessage: 'Failed to analyze TikTok profile',
        showInSystemMessages: true
    },

    analyzeInstagramProfile: {
        showInUI: true,
        startMessage: 'Analyzing your Instagram profile...',
        successMessage: 'Instagram profile analyzed!',
        errorMessage: 'Failed to analyze Instagram profile',
        showInSystemMessages: true
    },

    addCampaignAsset: {
        showInUI: true,
        startMessage: 'Saving your campaign assets...',
        successMessage: 'Campaign draft updated!',
        errorMessage: 'Failed to add assets to your campaign',
        showInSystemMessages: true
    },


    // Data analysis tools
    analyzeData: {
        showInUI: true,
        startMessage: 'Analyzing your data...',
        successMessage: 'Data analysis complete',
        errorMessage: 'Failed to analyze data',
        showInSystemMessages: true
    },

    presentQuickActions: {
        showInUI: true,
        startMessage: 'Presenting actions...',
        successMessage: 'Actions presented!',
        errorMessage: 'Failed to present actions',
        showInSystemMessages: true
    },

    pullBrandSpotifyReleases: {
        showInUI: true,
        startMessage: 'Pulling your latest Spotify releases...',
        successMessage: 'Spotify releases pulled!',
        errorMessage: 'Failed to pull Spotify releases',
        showInSystemMessages: true
    },

    updateAgentNotepad: {
        showInUI: false,
        showInSystemMessages: true
    },

    // Missing from SHARED_TOOLS
    downloadVideoFromUrl: {
        showInUI: true,
        startMessage: 'Downloading video...',
        successMessage: 'Video downloaded successfully',
        errorMessage: 'Failed to download video',
        showInSystemMessages: true
    },

    analyzeYouTubeProfile: {
        showInUI: true,
        startMessage: 'Analyzing YouTube channel...',
        successMessage: 'YouTube channel analyzed!',
        errorMessage: 'Failed to analyze YouTube channel',
        showInSystemMessages: true
    },

    searchConversationMemory: {
        showInUI: true,
        startMessage: 'Searching conversation history...',
        successMessage: 'Search complete',
        errorMessage: 'Failed to search conversation history',
        showInSystemMessages: true
    },

    getRecentMessages: {
        showInUI: true,
        startMessage: 'Retrieving recent messages...',
        successMessage: 'Messages retrieved',
        errorMessage: 'Failed to retrieve messages',
        showInSystemMessages: true
    },

    // Missing from SPECIALIST_TOOL_MAPPING
    removeCampaignAsset: {
        showInUI: true,
        startMessage: 'Removing campaign asset...',
        successMessage: 'Asset removed successfully',
        errorMessage: 'Failed to remove asset',
        showInSystemMessages: true
    },

    updateCampaignAsset: {
        showInUI: true,
        startMessage: 'Updating campaign asset...',
        successMessage: 'Asset updated successfully',
        errorMessage: 'Failed to update asset',
        showInSystemMessages: true
    },

    getRecentUploadedAssets: {
        showInUI: true,
        startMessage: 'Retrieving recent uploads...',
        successMessage: 'Recent uploads retrieved',
        errorMessage: 'Failed to retrieve recent uploads',
        showInSystemMessages: true
    },

    batchCreateCampaigns: {
        showInUI: true,
        startMessage: 'Creating multiple campaigns...',
        successMessage: 'Campaigns created successfully',
        errorMessage: 'Failed to create campaigns',
        showInSystemMessages: true
    },

    getMarketingPlansForBrand: {
        showInUI: true,
        startMessage: 'Retrieving marketing plans...',
        successMessage: 'Marketing plans retrieved',
        errorMessage: 'Failed to retrieve marketing plans',
        showInSystemMessages: true
    },

    getMarketingPlanById: {
        showInUI: true,
        startMessage: 'Retrieving marketing plan...',
        successMessage: 'Marketing plan retrieved',
        errorMessage: 'Failed to retrieve marketing plan',
        showInSystemMessages: true
    },

    updateMarketingPlan: {
        showInUI: true,
        startMessage: 'Updating marketing plan...',
        successMessage: 'Marketing plan updated!',
        errorMessage: 'Failed to update marketing plan',
        showInSystemMessages: true
    },

    updateMarketingPlanActionStatus: {
        showInUI: true,
        startMessage: 'Updating action status...',
        successMessage: 'Action status updated!',
        errorMessage: 'Failed to update action status',
        showInSystemMessages: true
    },

    searchPlatformInterestTargeting: {
        showInUI: true,
        startMessage: 'Searching targeting options...',
        successMessage: 'Targeting options found',
        errorMessage: 'Failed to search targeting options',
        showInSystemMessages: true
    },

    searchGeographies: {
        showInUI: true,
        startMessage: 'Searching locations...',
        successMessage: 'Locations found',
        errorMessage: 'Failed to search locations',
        showInSystemMessages: true
    },

    getMarketingCampaigns: {
        showInUI: true,
        startMessage: 'Retrieving campaigns...',
        successMessage: 'Campaigns retrieved',
        errorMessage: 'Failed to retrieve campaigns',
        showInSystemMessages: true
    },

    searchMarketingCampaigns: {
        showInUI: true,
        startMessage: 'Searching campaigns...',
        successMessage: 'Campaign search complete',
        errorMessage: 'Failed to search campaigns',
        showInSystemMessages: true
    },

    getCampaignsSnapshot: {
        showInUI: true,
        startMessage: 'Getting campaigns overview...',
        successMessage: 'Campaign snapshot retrieved',
        errorMessage: 'Failed to get campaign snapshot',
        showInSystemMessages: true
    },

    getCampaignOverallPerformance: {
        showInUI: true,
        startMessage: 'Analyzing campaign performance...',
        successMessage: 'Performance data retrieved',
        errorMessage: 'Failed to retrieve performance data',
        showInSystemMessages: true
    },

    getCampaignDailyStats: {
        showInUI: true,
        startMessage: 'Getting daily statistics...',
        successMessage: 'Daily stats retrieved',
        errorMessage: 'Failed to retrieve daily stats',
        showInSystemMessages: true
    },

    getCampaignGeographicBreakdown: {
        showInUI: true,
        startMessage: 'Analyzing geographic data...',
        successMessage: 'Geographic breakdown retrieved',
        errorMessage: 'Failed to retrieve geographic data',
        showInSystemMessages: true
    },

    getCampaignDemographicBreakdown: {
        showInUI: true,
        startMessage: 'Analyzing demographic data...',
        successMessage: 'Demographic breakdown retrieved',
        errorMessage: 'Failed to retrieve demographic data',
        showInSystemMessages: true
    },

    getCampaignAssetPerformance: {
        showInUI: true,
        startMessage: 'Analyzing asset performance...',
        successMessage: 'Asset performance data retrieved',
        errorMessage: 'Failed to retrieve asset performance',
        showInSystemMessages: true
    },

    getCampaignPlatformClicksBreakdown: {
        showInUI: true,
        startMessage: 'Analyzing platform clicks...',
        successMessage: 'Platform clicks data retrieved',
        errorMessage: 'Failed to retrieve platform clicks',
        showInSystemMessages: true
    },

    analyzeCampaignContent: {
        showInUI: true,
        startMessage: 'Analyzing campaign content...',
        successMessage: 'Content analysis complete',
        errorMessage: 'Failed to analyze content',
        showInSystemMessages: true
    },

    codeInterpreter: {
        showInUI: true,
        startMessage: 'Running custom analysis...',
        successMessage: 'Analysis complete',
        errorMessage: 'Analysis failed',
        showInSystemMessages: true
    }

    // Add more tool configurations as needed...
};

/** Default configuration for unknown tools */
const DEFAULT_TOOL_CONFIG: ToolCallConfig = {
    showInUI: true,
    startMessage: undefined, // Will use the original message from API
    successMessage: undefined,
    errorMessage: undefined,
    showInSystemMessages: true
};

/**
 * Get configuration for a specific tool
 */
export const getToolCallConfig = (toolName: string): ToolCallConfig => {
    return TOOL_CALL_CONFIG[toolName] || DEFAULT_TOOL_CONFIG;
};

/**
 * Check if a tool call should be shown in the UI
 */
export const shouldShowToolCall = (toolName: string): boolean => {
    const config = getToolCallConfig(toolName);
    return config.showInUI;
};

/**
 * Check if a tool call should be shown in system messages
 */
export const shouldShowInSystemMessages = (toolName: string): boolean => {
    const config = getToolCallConfig(toolName);
    return config.showInSystemMessages !== false; // Default to true if not specified
};

/**
 * Get custom message for tool call start
 */
export const getToolCallStartMessage = (toolName: string, originalMessage?: string): string => {
    const config = getToolCallConfig(toolName);
    return config.startMessage || originalMessage || `Working on ${toolName}...`;
};

/**
 * Get custom message for tool call success
 */
export const getToolCallSuccessMessage = (toolName: string, originalMessage?: string): string => {
    const config = getToolCallConfig(toolName);
    return config.successMessage || originalMessage || `${toolName} completed successfully`;
};

/**
 * Get custom message for tool call error
 */
export const getToolCallErrorMessage = (toolName: string, originalMessage?: string): string => {
    const config = getToolCallConfig(toolName);
    return config.errorMessage || originalMessage || `${toolName} failed`;
};

/**
 * Get the appropriate message based on tool call status
 */
export const getToolCallMessage = (
    toolName: string,
    status: 'running' | 'success' | 'error',
    originalMessage?: string
): string => {
    switch (status) {
        case 'running':
            return getToolCallStartMessage(toolName, originalMessage);
        case 'success':
            return getToolCallSuccessMessage(toolName, originalMessage);
        case 'error':
            return getToolCallErrorMessage(toolName, originalMessage);
        default:
            return originalMessage || `${toolName} - ${status}`;
    }
}; 